// Extraction mécanique de client.ts (WO-V3-02 / US-019) — méthodes API du domaine.
import { request, ApiError, httpMessage, BASE } from './http'
import { getToken } from '../auth/session'
import type { JobEvent } from './types/approvals'
import type { AssigneeKind, ErrorBudgetRow, BacklogItem, BacklogKind, BacklogNode, BacklogPriority, BornesSprint, BulkChanges, Burndown, Component, CumulativeFlow, FilterCriteria, ItemComment, ItemLink, KpiScorecard, Label, LinkType, PerimetrePlanSprints, PlanSprintsEnfile, ResultatPlanSprints, SavedFilter, Sprint, SprintInput, StoryDefinitionOfDone, Version, WorkspaceMember } from './types/backlog'
import type { InvitationAcceptance, MyInvitation, WorkspaceInvitation, WorkspaceTeam } from './types/collaboration'
import type { AppNotification, AuditBundle, AuditRoadmapPayload, BacklogItemInput, BacklogStats, ImpactReport, ItemActivityEvent, ProjectDoc, TraceabilityReport, UnreadNavigationNotification, WorkItemGraph } from './types/scmAudit'

export const backlogAgileAndCollaborationApi = {
  // --- Backlog agile (REQ-F-0009) ---

  /** Génère un backlog depuis une spécification APPROUVÉE — enfile un job SoD. */
  generateBacklog: (approvalId: number) =>
    request<{ job_id: string; source_approval: number; status: string }>(
      `/backlog/from-approval/${approvalId}`, { method: 'POST' }),

  /** Génère des OPTIONS depuis un CADRAGE approuvé — enfile un job SoD (REQ-F-0040). */
  proposeOptions: (approvalId: number) =>
    request<{ job_id: string; status: string }>(
      `/approvals/${approvalId}/options`, { method: 'POST' }),

  /** Dérive les PERSONAS depuis un CADRAGE approuvé — enfile un job SoD (ART-OUTIL-0132). */
  generatePersonas: (approvalId: number) =>
    request<{ job_id: string; source_approval: number; status: string }>(
      `/approvals/${approvalId}/personas`, { method: 'POST' }),

  /** Cartographie les PROCESSUS depuis un CADRAGE approuvé — enfile un job SoD (ART-OUTIL-0132). */
  mapProcesses: (approvalId: number) =>
    request<{ job_id: string; source_approval: number; status: string }>(
      `/approvals/${approvalId}/processes`, { method: 'POST' }),

  /**
   * Génère l'APPLICATION COMPLÈTE depuis une spécification APPROUVÉE — enfile un job SoD
   * (`scaffold_app`, PRM-0016). Le worker écrit l'arborescence et son manifeste de
   * provenance ; l'artefact revient en approbation comme tout autre, pas par un circuit à part.
   *
   * L'espace est dans le CHEMIN, mais la passerelle ne le croit pas : elle le confronte à
   * l'espace de l'approbation et répond 404 en cas d'écart. Le locataire, lui, n'est jamais
   * envoyé — il vient du jeton. Ne sont transmis ici que des IDENTIFIANTS d'items acceptés :
   * leur contenu est relu en base côté serveur.
   */
  scaffoldApp: (
    workspaceId: string,
    corps: {
      approval_id: number
      app_slug: string
      app_name?: string
      app_port: number
      backlog_item_ids?: number[]
    },
  ) =>
    request<{
      job_id: string
      source_approval: number
      app_slug: string
      app_port: number
      backlog_items: number
      status: string
    }>(
      `/apps/${encodeURIComponent(workspaceId)}/scaffold`,
      { method: 'POST', body: JSON.stringify(corps) }),

  /** Dérive les EPICS depuis un CADRAGE approuvé — cascade IA par item (EPIC-CASCADE). */
  generateEpics: (approvalId: number) =>
    request<{ job_id: string; source_approval: number; status: string }>(
      `/approvals/${approvalId}/epics`, { method: 'POST' }),

  /** Rédige la SPÉCIFICATION depuis un CADRAGE approuvé — circuit artefact + approbation. */
  draftSpecFromCadrage: (approvalId: number) =>
    request<{ job_id: string; source_approval: number; status: string }>(
      `/approvals/${approvalId}/specification`, { method: 'POST' }),

  /** Les items du board, filtres facultatifs (kind/priority/epic_id). */
  backlogItems: (
    workspaceId: string,
    filtres?: {
      kind?: BacklogKind; priority?: BacklogPriority; epic_id?: number; team_id?: string
      /** Lot AG (manque 15) : sprint EFFECTIF (id ou `none`), assigné (`me`, `none`, `agent`, user_id),
       *  statut, étiquette et texte (`#12` cherche l'identifiant). */
      sprint?: string; assignee?: string; status?: string; label_id?: number; q?: string
    },
  ) => {
    const params = new URLSearchParams({ workspace_id: workspaceId })
    if (filtres?.kind) params.set('kind', filtres.kind)
    if (filtres?.priority) params.set('priority', filtres.priority)
    if (filtres?.epic_id != null) params.set('epic_id', String(filtres.epic_id))
    if (filtres?.team_id) params.set('team_id', filtres.team_id)
    if (filtres?.sprint) params.set('sprint', filtres.sprint)
    if (filtres?.assignee) params.set('assignee', filtres.assignee)
    if (filtres?.status) params.set('status', filtres.status)
    if (filtres?.label_id != null) params.set('label_id', String(filtres.label_id))
    if (filtres?.q) params.set('q', filtres.q)
    return request<BacklogItem[]>(`/backlog?${params.toString()}`)
  },

  /** Un item unique par id — socle de la page item deep-linkable (/items/:id). Réexpose
   *  workspace_id (nécessaire pour fixer l'espace actif et charger les items frères). */
  getBacklogItem: (id: number) =>
    request<BacklogItem & { workspace_id: string }>(`/backlog/items/${id}`),

  backlogStats: (workspaceId: string) =>
    request<BacklogStats>(`/backlog/stats?workspace_id=${encodeURIComponent(workspaceId)}`),

  /** L'arbre unifié des work items (Phase 2) : Epic→Feature→US→Tâche imbriqués par parent. */
  backlogTree: (workspaceId: string) =>
    request<BacklogNode[]>(`/backlog/tree?workspace_id=${encodeURIComponent(workspaceId)}`),

  // ---- Sprints agiles (Phase 3) -----------------------------------------
  listSprints: (workspaceId: string) =>
    request<Sprint[]>(`/sprints?workspace_id=${encodeURIComponent(workspaceId)}`),

  createSprint: (body: SprintInput) =>
    request<Sprint>('/sprints', { method: 'POST', body: JSON.stringify(body) }),

  patchSprint: (
    id: number,
    patch: Partial<Pick<Sprint, 'name' | 'goal' | 'starts_on' | 'ends_on' | 'status'>>,
  ) =>
    request<Sprint>(`/sprints/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  /** Le burndown d'un sprint : série idéal vs restant par jour (nécessite des dates). */
  sprintBurndown: (id: number) =>
    request<Burndown>(`/sprints/${id}/burndown`),

  // ---- Planification de sprints assistée (`plan_sprints`) ----------------
  //
  // Une capacité qui existait sans être atteignable : le worker savait juger un plan de
  // sprints (clôture amont ordonnée, bornes de taille), mais aucune route ne l'enfilait et
  // aucun écran ne l'appelait. Les trois méthodes ci-dessous ferment la chaîne.

  /**
   * Ce que la génération VA lire, compté par la passerelle : items hors sprint, items à
   * replanifier, liens bloquants. Appelé AVANT la génération — l'utilisateur voit et
   * corrige son périmètre avant de payer, pas après l'avoir payé.
   */
  sprintPlanScope: (workspaceId: string) =>
    request<PerimetrePlanSprints>(
      `/sprints/plan/perimetre?workspace_id=${encodeURIComponent(workspaceId)}`),

  /**
   * Enfile un job `plan_sprints`. Le corps ne porte que les BORNES : le périmètre est relu
   * en base par le worker et le verdict y est recalculé, donc rien de ce qui fait autorité
   * ne transite par le client.
   */
  planSprints: (workspaceId: string, bornes?: Partial<BornesSprint>) =>
    request<PlanSprintsEnfile>('/sprints/plan', {
      method: 'POST',
      body: JSON.stringify({ workspace_id: workspaceId, ...(bornes ? { bornes } : {}) }),
    }),

  /**
   * Le bilan du job : proposition du modèle, verdict RECALCULÉ par le runtime, mouvements
   * qu'appliquer produirait. Rien n'a été écrit au backlog — `applique` est faux.
   */
  sprintPlanResult: (jobId: string, workspaceId: string) =>
    request<ResultatPlanSprints>(
      `/sprints/plan/resultat/${encodeURIComponent(jobId)}`
      + `?workspace_id=${encodeURIComponent(workspaceId)}`),

  /** Le flux cumulé de l'espace : items par statut dans le temps (reconstruit du journal). */
  cumulativeFlow: (workspaceId: string) =>
    request<CumulativeFlow>(
      `/backlog/cumulative-flow?workspace_id=${encodeURIComponent(workspaceId)}`),

  // ---- Labels + liens (Phase 3) -----------------------------------------
  listLabels: (workspaceId: string) =>
    request<Label[]>(`/labels?workspace_id=${encodeURIComponent(workspaceId)}`),

  createLabel: (workspaceId: string, name: string, color: string) =>
    request<Label>('/labels',
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, name, color }) }),

  deleteLabel: (id: number) =>
    request<{ id: number; deleted: boolean; items_affected?: number }>(`/labels/${id}`, { method: 'DELETE' }),

  addItemLabel: (itemId: number, labelId: number) =>
    request<{ linked: boolean }>(`/backlog/${itemId}/labels/${labelId}`, { method: 'POST' }),

  removeItemLabel: (itemId: number, labelId: number) =>
    request<{ linked: boolean }>(`/backlog/${itemId}/labels/${labelId}`, { method: 'DELETE' }),

  listItemLinks: (itemId: number) =>
    request<ItemLink[]>(`/backlog/${itemId}/links`),

  createItemLink: (itemId: number, toItemId: number, linkType: LinkType) =>
    request<{ id: number; link_type: LinkType }>(`/backlog/${itemId}/links`,
      { method: 'POST', body: JSON.stringify({ to_item_id: toItemId, link_type: linkType }) }),

  deleteItemLink: (linkId: number) =>
    request<{ id: number; deleted: boolean }>(`/backlog/links/${linkId}`, { method: 'DELETE' }),

  // ---- Filtres sauvegardés + édition en masse (Phase 3) -----------------
  listFilters: (workspaceId: string) =>
    request<SavedFilter[]>(`/filters?workspace_id=${encodeURIComponent(workspaceId)}`),

  createFilter: (workspaceId: string, name: string, criteria: FilterCriteria) =>
    request<SavedFilter>('/filters',
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, name, criteria }) }),

  deleteFilter: (id: number) =>
    request<{ id: number; deleted: boolean }>(`/filters/${id}`, { method: 'DELETE' }),

  /** Applique priorité / statut / sprint aux items désignés ; renvoie le nombre modifié. */
  bulkEditItems: (workspaceId: string, ids: number[], changes: BulkChanges) =>
    request<{ updated: number }>('/backlog/bulk',
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, ids, ...changes }) }),

  // ---- Composants + versions (Phase 3) ----------------------------------
  listComponents: (workspaceId: string) =>
    request<Component[]>(`/components?workspace_id=${encodeURIComponent(workspaceId)}`),
  createComponent: (workspaceId: string, name: string) =>
    request<Component>('/components',
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, name }) }),
  deleteComponent: (id: number) =>
    request<{ id: number; deleted: boolean; items_affected?: number }>(`/components/${id}`, { method: 'DELETE' }),

  listVersions: (workspaceId: string) =>
    request<Version[]>(`/versions?workspace_id=${encodeURIComponent(workspaceId)}`),
  createVersion: (workspaceId: string, name: string, releaseDate?: string) =>
    request<Version>('/versions', {
      method: 'POST',
      body: JSON.stringify({ workspace_id: workspaceId, name,
        ...(releaseDate ? { release_date: releaseDate } : {}) }),
    }),
  /** Lot FON-B : renommer (`name`), dater, marquer livrée ; champ absent = inchangé. */
  patchVersion: (id: number, patch: { name?: string; released?: boolean; release_date?: string }) =>
    request<Version>(`/versions/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteVersion: (id: number) =>
    request<{ id: number; deleted: boolean; items_affected?: number }>(`/versions/${id}`, { method: 'DELETE' }),

  /** Range un item dans un composant (ou l'en retire si null). */
  assignItemComponent: (itemId: number, componentId: number | null) =>
    request<BacklogItem>(`/backlog/${itemId}/component`,
      { method: 'PATCH', body: JSON.stringify({ component_id: componentId }) }),
  /** Affecte un item à une version cible (ou l'en retire si null). */
  assignItemVersion: (itemId: number, versionId: number | null) =>
    request<BacklogItem>(`/backlog/${itemId}/version`,
      { method: 'PATCH', body: JSON.stringify({ version_id: versionId }) }),

  /** Range un item dans un sprint (ou l'en retire si sprintId est null). */
  assignItemToSprint: (id: number, sprintId: number | null) =>
    request<BacklogItem>(`/backlog/${id}/sprint`,
      { method: 'PATCH', body: JSON.stringify({ sprint_id: sprintId }) }),
  assignItemToTeam: (id: number, teamId: string | null) =>
    request<BacklogItem>(`/backlog/${id}/team`,
      { method: 'PATCH', body: JSON.stringify({ team_id: teamId }) }),

  // ---- Collaboration : commentaires + @mentions (Phase 4) ---------------
  listWorkspaceMembers: (workspaceId: string) =>
    request<WorkspaceMember[]>(
      `/workspaces/${encodeURIComponent(workspaceId)}/members`),

  // ---- Gestion des membres (D4) : réservée à qui administre le projet (propriétaire, administration — ARB-AUD-08) ----
  /** Ajoute un membre existant du locataire ({@code user} = user_id OU username). */
  addWorkspaceMember: (workspaceId: string, user: string, role: string) =>
    request<{ workspace_id: string; user_id: string; role: string }>(
      `/workspaces/${encodeURIComponent(workspaceId)}/members`,
      { method: 'POST', body: JSON.stringify({ user, role }) }),

  /** Change le rôle d'un membre (owner / contributor / viewer). */
  updateWorkspaceMemberRole: (workspaceId: string, userId: string, role: string) =>
    request<{ workspace_id: string; user_id: string; role: string }>(
      `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) }),

  /** Retire un membre de l'espace. */
  removeWorkspaceMember: (workspaceId: string, userId: string) =>
    request<void>(
      `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }),
  listWorkspaceInvitations: (workspaceId: string) => request<WorkspaceInvitation[]>(`/workspaces/${encodeURIComponent(workspaceId)}/invitations`),
  inviteWorkspaceMember: (workspaceId: string, email: string, role: string) => request<{ invitation_id: string; token: string; expires_in_hours: number }>(`/workspaces/${encodeURIComponent(workspaceId)}/invitations`, { method: 'POST', body: JSON.stringify({ email, role }) }),
  revokeWorkspaceInvitation: (workspaceId: string, invitationId: string) => request<{ invitation_id: string; status: string }>(`/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(invitationId)}/revoke`, { method: 'POST' }),
  /** FND-V050-050 : MES invitations encore utilisables (boîte de l'invité). */
  listMyInvitations: () => request<MyInvitation[]>('/me/invitations'),
  /** Acceptation depuis la boîte (e-mail attesté exigé par la passerelle). */
  acceptMyInvitation: (invitationId: string) => request<InvitationAcceptance>(`/me/invitations/${encodeURIComponent(invitationId)}/accept`, { method: 'POST' }),
  /** Acceptation par le lien d'invitation : le jeton voyage dans le CORPS, jamais dans l'URL. */
  acceptInvitationToken: (token: string) => request<InvitationAcceptance>('/invitations/accept', { method: 'POST', body: JSON.stringify({ token }) }),
  offboardWorkspaceMember: (workspaceId: string, userId: string) => request<{ offboarding_id: string; user_id: string; access_revoked: boolean }>(`/users/${encodeURIComponent(userId)}/offboarding`, { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }) }),

  // ---- Equipes de travail (multi-équipe) ---------------------------------
  listWorkspaceTeams: (workspaceId: string) =>
    request<WorkspaceTeam[]>(`/workspaces/${encodeURIComponent(workspaceId)}/teams`),
  createWorkspaceTeam: (workspaceId: string, name: string, description?: string) =>
    request<{ team_id: string; workspace_id: string; name: string }>(
      `/workspaces/${encodeURIComponent(workspaceId)}/teams`, {
        method: 'POST', body: JSON.stringify({ name, description }),
      }),
  addWorkspaceTeamMember: (workspaceId: string, teamId: string, userId: string) =>
    request<{ team_id: string; user_id: string }>(
      `/workspaces/${encodeURIComponent(workspaceId)}/teams/${encodeURIComponent(teamId)}/members`, {
        method: 'POST', body: JSON.stringify({ user_id: userId }),
      }),
  removeWorkspaceTeamMember: (workspaceId: string, teamId: string, userId: string) =>
    request<void>(
      `/workspaces/${encodeURIComponent(workspaceId)}/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }),
  deleteWorkspaceTeam: (workspaceId: string, teamId: string) =>
    request<void>(
      `/workspaces/${encodeURIComponent(workspaceId)}/teams/${encodeURIComponent(teamId)}`,
      { method: 'DELETE' }),

  listItemComments: (itemId: number) =>
    request<ItemComment[]>(`/backlog/${itemId}/comments`),

  /** Publie un commentaire ; les @pseudos membres sont résolus côté serveur. */
  addItemComment: (itemId: number, body: string) =>
    request<ItemComment>(`/backlog/${itemId}/comments`,
      { method: 'POST', body: JSON.stringify({ body }) }),

  // ---- Notifications in-app (Phase 4) -----------------------------------
  listNotifications: (workspaceId: string, unread = false) =>
    request<AppNotification[]>(`/notifications?workspace_id=${encodeURIComponent(workspaceId)}${unread ? '&unread=true' : ''}`),

  /** Compteur de non-lues — alimente le badge de navigation. */
  unreadNotificationsCount: (workspaceId: string) =>
    request<{ count: number }>(`/notifications/unread-count?workspace_id=${encodeURIComponent(workspaceId)}`),
  unreadNotificationsByNav: (workspaceId: string) =>
    request<{ scope: { organisationId: string; workspaceId: string }; items: UnreadNavigationNotification[]; unknownCount: number }>(
      `/notifications/unread-by-nav?workspace_id=${encodeURIComponent(workspaceId)}`),

  markNotificationRead: (id: number, workspaceId: string) =>
    request<{ id: number; read: boolean }>(`/notifications/${id}/read?workspace_id=${encodeURIComponent(workspaceId)}`, { method: 'POST' }),

  markAllNotificationsRead: (workspaceId: string) =>
    request<{ updated: number }>(`/notifications/read-all?workspace_id=${encodeURIComponent(workspaceId)}`, { method: 'POST' }),

  createBacklogItem: (body: BacklogItemInput) =>
    request<BacklogItem>('/backlog', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Déplace une carte : changement de colonne (statut) sur le board Kanban. Le statut est
   * une `string` (et non le seul BacklogStatus) car les colonnes dérivent du template effectif
   * (F1b). Le serveur reste l'autorité : une clé hors des valeurs autorisées (contrainte CHECK
   * chk_backlog_status) est refusée côté API, pas masquée ici.
   */
  moveBacklogItem: (id: number, status: string, boardRank?: number) =>
    request<BacklogItem>(`/backlog/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(boardRank != null ? { status, board_rank: boardRank } : { status }),
    }),

  /** RJ-45 — Definition of Done d'une story, calculée AVANT le clic : cas relié conforme, tâches closes. */
  getStoryDefinitionOfDone: (id: number) =>
    request<StoryDefinitionOfDone>(`/backlog/items/${id}/definition-of-done`),
  /** RJ-45 — relie le cas de recette (ou le test) qui prouve la story ; rend la DoD recalculée. */
  linkProvingTestCase: (id: number, testCaseId: number) =>
    request<StoryDefinitionOfDone>(`/backlog/items/${id}/proving-cases`, {
      method: 'POST', body: JSON.stringify({ test_case_id: testCaseId }),
    }),

  /** Assigne à un humain OU un agent — passer `null` aux deux pour désassigner. */
  assignBacklogItem: (id: number, kind: AssigneeKind | null, ref: string | null) =>
    request<BacklogItem>(`/backlog/${id}/assign`, {
      method: 'PATCH',
      body: JSON.stringify({ assignee_kind: kind, assignee_ref: ref }),
    }),

  /** Lot FON-B : `withChildren` confirme la suppression des descendants (sinon refus nommé). */
  deleteBacklogItem: (id: number, withChildren = false) =>
    request<{ id: number; deleted: boolean; descendants_deleted?: number }>(
      `/backlog/${id}${withChildren ? '?with_children=true' : ''}`, { method: 'DELETE' }),

  /**
   * Édite un item (co-écriture) : seuls les champs fournis changent (PATCH partiel).
   * Ne touche ni au statut Kanban ni à l'acceptation.
   */
  patchBacklogItem: (
    id: number,
    patch: Partial<Pick<BacklogItem,
      'title' | 'description' | 'acceptance_criteria' | 'priority' | 'effort_points'
      | 'as_a' | 'i_want' | 'so_that' | 'custom_fields'>>,
  ) =>
    request<BacklogItem>(`/backlog/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  /** Génère un diff depuis une tâche/bug accepté (T2). */
  implementWorkItem: (id: number, scopeFiles: string[], constraints?: string) =>
    request<{ job_id: string; source_story: number; source_item?: number; status: string }>(
      `/backlog/${id}/implement`, {
        method: 'POST',
        body: JSON.stringify(
          constraints ? { scope_files: scopeFiles, constraints } : { scope_files: scopeFiles }),
      }),

  // G0 (B-010, ART-0166) : `implementStory` (alias de compatibilité, jamais appelé —
  // `ItemDetail.tsx` appelle `implementWorkItem` directement) a été retiré. Chemin vérifié :
  // aucun appelant dans l'interface, les scripts ou les tests d'intégration — décision
  // tracée dans `docs/doit-specification/00-governance/decision-log.md` (DEC-024).

  /** Story → analyse du dépôt (analyze_repository, S3). Première étape du triptyque. */
  analyzeStory: (id: number, scopeFiles: string[]) =>
    request<{ job_id: string; source_story: number; status: string }>(
      `/backlog/${id}/analyze`, {
        method: 'POST',
        body: JSON.stringify({ scope_files: scopeFiles }),
      }),

  /** Story → revue de code (code_review, S3) du dernier changement APPROUVÉ. */
  reviewStory: (id: number) =>
    request<{ job_id: string; source_story: number; status: string }>(
      `/backlog/${id}/review`, { method: 'POST' }),

  /** Story → conception de tests (design_tests) : la patte TEST de la traçabilité. */
  designTests: (id: number, scopeFiles: string[]) =>
    // O39 : `source_story` = la story dont les critères sont couverts (la parente d'une tâche) ;
    // `requested_from` = l'item depuis lequel la conception a été demandée.
    request<{ job_id: string; source_story: number; requested_from?: number; status: string }>(
      `/backlog/${id}/tests`, {
        method: 'POST',
        body: JSON.stringify({ scope_files: scopeFiles }),
      }),

  /**
   * Cascade IA par item : génère les ENFANTS d'un item (epic→features, feature→US,
   * US→tâches). Le niveau enfant est déduit du `kind` parent côté serveur ; les enfants
   * entrent au board en `acceptance_status='pending'` (EPIC-CASCADE).
   */
  generateChildren: (id: number) =>
    request<{ job_id: string; source_item: number; kind: BacklogKind; status: string }>(
      `/backlog/${id}/generate-children`, { method: 'POST' }),

  /**
   * Accepte un item généré (SoD-01 allégée) : refusé côté serveur si l'appelant en est le
   * générateur, ou si l'item n'est pas en attente. Renvoie l'item accepté.
   */
  acceptBacklogItem: (id: number) =>
    request<BacklogItem>(`/backlog/${id}/accept`, { method: 'PATCH' }),

  /**
   * Suit la progression d'un job — la « glass box » (ADR-R0013).
   *
   * `EventSource` ne sait pas porter d'en-tête Authorization : on lit donc le flux
   * SSE via fetch et on le décode trame par trame. Le serveur clôt de lui-même
   * après un événement terminal ; l'appelant peut interrompre via le signal.
   */
  streamJobEvents: async (
    jobId: string,
    onEvent: (event: JobEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const token = getToken()
    const response = await fetch(`${BASE}/jobs/${encodeURIComponent(jobId)}/events`, {
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      // `exactOptionalPropertyTypes` : fetch attend AbortSignal | null, pas undefined.
      signal: signal ?? null,
    })
    if (!response.ok || !response.body) {
      throw new ApiError(response.status, httpMessage(response.status))
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      // Une trame SSE se termine par une ligne vide ; le reste attend la suite.
      const frames = buffer.split('\n\n')
      buffer = frames.pop() ?? ''
      for (const frame of frames) {
        const dataLines = frame
          .split('\n')
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).trim())
        if (dataLines.length === 0) continue
        try {
          onEvent(JSON.parse(dataLines.join('')) as JobEvent)
        } catch {
          // Trame illisible : ignorée plutôt que fatale — la table reste la
          // vérité, un rechargement rejoue tout.
        }
      }
    }
  },

  /** Fil d'activité unifié d'un item (création, statuts, commentaires, pièces jointes). */
  listItemActivity: (itemId: number) =>
    request<ItemActivityEvent[]>(`/backlog/${itemId}/activity`),

  /** Export d'audit d'un espace : bundle gouvernance + chaîne de hachage recalculée. */
  exportAudit: (workspaceId: string, pseudonymise = false) =>
    request<AuditBundle>(
      `/audit/export?workspace_id=${encodeURIComponent(workspaceId)}`
      // La passerelle accepte ce paramètre depuis V46 ; aucun écran ne l'envoyait, et la
      // pseudonymisation n'était donc atteignable que par appel direct à l'API.
      + `&pseudonymise=${pseudonymise}`),

  /** Backlog d'audit interne : le serveur applique owner/admin avant de livrer le corpus. */
  auditRoadmap: () => request<AuditRoadmapPayload>('/admin/audit-roadmap'),

  /** Analyse d'impact : les items affectés en aval si celui-ci change (arbre + liens). */
  itemImpact: (itemId: number) =>
    request<ImpactReport>(`/backlog/${itemId}/impact`),

  /** Graphe (DAG) des work items d'un espace : nœuds + liens hiérarchie/dépendance. */
  backlogGraph: (workspaceId: string) =>
    request<WorkItemGraph>(`/backlog/graph?workspace_id=${encodeURIComponent(workspaceId)}`),

  /** Traçabilité REQ↔code↔test d'un espace : couverture code/test par exigence + trous. */
  traceability: (workspaceId: string) =>
    request<TraceabilityReport>(
      `/traceability?workspace_id=${encodeURIComponent(workspaceId)}`),

  /** Scorecard KPI / SLI-SLO d'un espace : livraison, génération, gouvernance + SLO. */
  /** Budgets d'erreur de l'espace (`GET /kpi/error-budget`) : signal « Exploitation » de la santé v1.1. */
  kpiErrorBudget: (workspaceId: string) =>
    request<ErrorBudgetRow[]>(`/kpi/error-budget?workspace_id=${encodeURIComponent(workspaceId)}`),
  kpiScorecard: (workspaceId: string) =>
    request<KpiScorecard>(`/kpi?workspace_id=${encodeURIComponent(workspaceId)}`),

  /** Télécharge l'export ZIP d'un espace (données JSON + manifeste) — souverain. */
  exportWorkspaceZip: async (workspaceId: string): Promise<void> => {
    const token = getToken()
    const response = await fetch(
      `${BASE}/workspaces/${encodeURIComponent(workspaceId)}/export`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    if (!response.ok) {
      throw new ApiError(response.status, httpMessage(response.status),
        await response.text().catch(() => undefined))
    }
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `workspace-${workspaceId}.zip`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },

  /** Documentation vivante du projet : Markdown régénéré des données courantes. */
  projectDoc: (workspaceId: string) =>
    request<ProjectDoc>(`/docs/project?workspace_id=${encodeURIComponent(workspaceId)}`),

}
